import type { AgentSetEvent } from '@autobid/ui/types/websocket/AgentSetEvent'
import type { AuctionFinishEvent } from '@autobid/ui/types/websocket/AuctionFinishEvent'
import type { AuctionHiddenEvent } from '@autobid/ui/types/websocket/AuctionHiddenEvent'
import type { AuctionInPrepareEvent } from '@autobid/ui/types/websocket/AuctionInPrepareEvent'
import type { AuctionPauseEvent } from '@autobid/ui/types/websocket/AuctionPauseEvent'
import type { AuctionPublishEvent } from '@autobid/ui/types/websocket/AuctionPublishEvent'
import type { AuctionResumeEvent } from '@autobid/ui/types/websocket/AuctionResumeEvent'
import type { AuctionStartHotbidEvent } from '@autobid/ui/types/websocket/AuctionStartHotbidEvent'
import type { AuctionTimeChange } from '@autobid/ui/types/websocket/AuctionTimeChange'
import type { AuctionVisibleEvent } from '@autobid/ui/types/websocket/AuctionVisibleEvent'
import type { AuctionWithdrawEvent } from '@autobid/ui/types/websocket/AuctionWithdrawEvent'
import type { BidDeletedEvent } from '@autobid/ui/types/websocket/BidDeletedEvent'
import type { BidOnAgentEvent } from '@autobid/ui/types/websocket/BidOnAgentEvent'
import type { BidOptionsEvent } from '@autobid/ui/types/websocket/BidOptionsEvent'
import type { CarActiveSoonInHotbidEvent } from '@autobid/ui/types/websocket/CarActiveSoonInHotbidEvent'
import type { CarEnterHotbidEvent } from '@autobid/ui/types/websocket/CarEnterHotbidEvent'
import type { CarFinishedEvent } from '@autobid/ui/types/websocket/CarFinishedEvent'
import type { CarHotbidResetEvent } from '@autobid/ui/types/websocket/CarHotbidReset'
import type { CarInPrepareEvent } from '@autobid/ui/types/websocket/CarInPrepareEvent'
import type { CarNotSoldEvent } from '@autobid/ui/types/websocket/CarNotSoldEvent'
import type { CarOverbidEvent } from '@autobid/ui/types/websocket/CarOverbidEvent'
import type { CarReservedEvent } from '@autobid/ui/types/websocket/CarReservedEvent'
import type { CarSoldEvent } from '@autobid/ui/types/websocket/CarSoldEvent'
import type { CarWatchedFinishedEvent } from '@autobid/ui/types/websocket/CarWatchedFinishedEvent'
import type { CarWatchedSoonInHotbidEvent } from '@autobid/ui/types/websocket/CarWatchedSoonInHotbidEvent'
import type { DocumentInvoiceEvent } from '@autobid/ui/types/websocket/DocumentInvoiceEvent'
import type { DocumentPickupEvent } from '@autobid/ui/types/websocket/DocumentPickupEvent'
import type { ItemDeleteEvent } from '@autobid/ui/types/websocket/ItemDeleteEvent'
import type { ItemPublishEvent } from '@autobid/ui/types/websocket/ItemPublishEvent'
import type { ItemReserveReleaseEvent } from '@autobid/ui/types/websocket/ItemReserveReleaseEvent'
import type { ItemWithdrawEvent } from '@autobid/ui/types/websocket/ItemWithdrawEvent'
import type { TransportReminderEvent } from '@autobid/ui/types/websocket/TransportReminderEvent'
import type { UserBidEvent } from '@autobid/ui/types/websocket/UserBidEvent'
import type { UserBoughtCarEvent } from '@autobid/ui/types/websocket/UserBoughtCarEvent'
import type { UserBoughtCarReservedEvent } from '@autobid/ui/types/websocket/UserBoughtCarReservedEvent'
import type { UserNewNotificationEvent } from '@autobid/ui/types/websocket/UserNewNotificationEvent'
import type { UserNotBoughtCarEvent } from '@autobid/ui/types/websocket/UserNotBoughtCarEvent'
import type { UserNotSoldCarEvent } from '@autobid/ui/types/websocket/UserNotSoldCarEvent'
import type { UserSearchCriteriaFoundCarEvent } from '@autobid/ui/types/websocket/UserSearchCriteriaFoundCarEvent'
import type { UserSoldCarEvent } from '@autobid/ui/types/websocket/UserSoldCarEvent'
import { defineStore } from 'pinia'
import { ref } from 'vue'

// #region types
/**
 * Currently used events, feel free to add new events that are supported by courier
 */
export type SocketEventName =
  | 'premium.BID_OPTIONS'
  | 'premium.AGENT_SET'
  | 'premium.AUCTION_PUBLISH'
  | 'premium.AUCTION_WITHDRAW'
  | 'premium.AUCTION_IN_PREPARE'
  | 'premium.AUCTION_HIDDEN'
  | 'premium.AUCTION_FINISH'
  | 'premium.AUCTION_VISIBLE'
  | 'premium.AUCTION_START_HOTBID'
  | 'premium.CAR_OVERBID'
  | 'premium.CAR_WATCHED_SOON_IN_HOTBID'
  | 'premium.CAR_ACTIVE_SOON_IN_HOTBID'
  | 'premium.USER_BOUGHT_CAR'
  | 'premium.USER_NOT_BOUGHT_CAR'
  | 'premium.DOCUMENT_PICKUP'
  | 'premium.USER_SOLD_CAR'
  | 'premium.CAR_RESERVED'
  | 'premium.USER_NOT_SOLD_CAR'
  | 'premium.USER_SEARCH_CRITERIA_FOUND_CAR'
  | 'premium.DOCUMENT_INVOICE'
  | 'premium.USER_BOUGHT_CAR_RESERVED'
  | 'premium.CAR_WATCHED_FINISHED'
  | 'premium.TRANSPORT_REMINDER'
  | 'premium.USER_NEW_NOTIFICATION'
  | 'premium.BID_DELETED'
  | 'premium.ITEM_PUBLISH'
  | 'premium.USER_BID'
  | 'premium.AUCTION_PAUSE'
  | 'premium.AUCTION_RESUME'
  | 'premium.AUCTION_TIME_CHANGE'
  | 'premium.CAR_ENTER_HOTBID'
  | 'premium.CAR_FINISHED'
  | 'premium.CAR_HOTBID_RESET'
  | 'premium.CAR_IN_PREPARE'
  | 'premium.CAR_NOT_SOLD'
  | 'premium.CAR_SOLD'
  | 'premium.ITEM_RESERVE_RELEASE'
  | 'premium.ITEM_WITHDRAW'
  | 'premium.ITEM_DELETED'
  | 'premium.USER_BID_ON_AGENT'

interface BidOptionsProps {
  eventName: 'premium.BID_OPTIONS'
  callback: (e: BidOptionsEvent) => void
}
interface AgentSetProps {
  eventName: 'premium.AGENT_SET'
  callback: (e: AgentSetEvent) => void
}
interface AuctionPublishProps {
  eventName: 'premium.AUCTION_PUBLISH'
  callback: (e: AuctionPublishEvent) => void
}
interface AuctionWithdrawProps {
  eventName: 'premium.AUCTION_WITHDRAW'
  callback: (e: AuctionWithdrawEvent) => void
}
interface AuctionInPrepareProps {
  eventName: 'premium.AUCTION_IN_PREPARE'
  callback: (e: AuctionInPrepareEvent) => void
}
interface AuctionHiddenProps {
  eventName: 'premium.AUCTION_HIDDEN'
  callback: (e: AuctionHiddenEvent) => void
}
interface AuctionFinishProps {
  eventName: 'premium.AUCTION_FINISH'
  callback: (e: AuctionFinishEvent) => void
}
interface AuctionVisibleProps {
  eventName: 'premium.AUCTION_VISIBLE'
  callback: (e: AuctionVisibleEvent) => void
}
interface AuctionStartHotbidProps {
  eventName: 'premium.AUCTION_START_HOTBID'
  callback: (e: AuctionStartHotbidEvent) => void
}
interface CarOverbidProps {
  eventName: 'premium.CAR_OVERBID'
  callback: (e: CarOverbidEvent) => void
}
interface CarWatchedSoonInHotbidProps {
  eventName: 'premium.CAR_WATCHED_SOON_IN_HOTBID'
  callback: (e: CarWatchedSoonInHotbidEvent) => void
}
interface CarActiveSoonInHotbidProps {
  eventName: 'premium.CAR_ACTIVE_SOON_IN_HOTBID'
  callback: (e: CarActiveSoonInHotbidEvent) => void
}
interface UserBoughtCarProps {
  eventName: 'premium.USER_BOUGHT_CAR'
  callback: (e: UserBoughtCarEvent) => void
}
interface UserNotBoughtCarProps {
  eventName: 'premium.USER_NOT_BOUGHT_CAR'
  callback: (e: UserNotBoughtCarEvent) => void
}
interface DocumentPickupProps {
  eventName: 'premium.DOCUMENT_PICKUP'
  callback: (e: DocumentPickupEvent) => void
}
interface UserSoldCarProps {
  eventName: 'premium.USER_SOLD_CAR'
  callback: (e: UserSoldCarEvent) => void
}
interface CarReservedProps {
  eventName: 'premium.CAR_RESERVED'
  callback: (e: CarReservedEvent) => void
}
interface UserNotSoldCarProps {
  eventName: 'premium.USER_NOT_SOLD_CAR'
  callback: (e: UserNotSoldCarEvent) => void
}
interface UserSearchCriteriaFoundCarProps {
  eventName: 'premium.USER_SEARCH_CRITERIA_FOUND_CAR'
  callback: (e: UserSearchCriteriaFoundCarEvent) => void
}
interface DocumentInvoiceProps {
  eventName: 'premium.DOCUMENT_INVOICE'
  callback: (e: DocumentInvoiceEvent) => void
}
interface UserBoughtCarReservedProps {
  eventName: 'premium.USER_BOUGHT_CAR_RESERVED'
  callback: (e: UserBoughtCarReservedEvent) => void
}
interface CarWatchedFinishedProps {
  eventName: 'premium.CAR_WATCHED_FINISHED'
  callback: (e: CarWatchedFinishedEvent) => void
}
interface TransportReminderProps {
  eventName: 'premium.TRANSPORT_REMINDER'
  callback: (e: TransportReminderEvent) => void
}
interface UserNewNotificationProps {
  eventName: 'premium.USER_NEW_NOTIFICATION'
  callback: (e: UserNewNotificationEvent) => void
}
interface BidDeletedProps {
  eventName: 'premium.BID_DELETED'
  callback: (e: BidDeletedEvent) => void
}
interface ItemPublishProps {
  eventName: 'premium.ITEM_PUBLISH'
  callback: (e: ItemPublishEvent) => void
}
interface UserBidProps {
  eventName: 'premium.USER_BID'
  callback: (e: UserBidEvent) => void
}
interface AuctionPauseProps {
  eventName: 'premium.AUCTION_PAUSE'
  callback: (e: AuctionPauseEvent) => void
}
interface AuctionResumeProps {
  eventName: 'premium.AUCTION_RESUME'
  callback: (e: AuctionResumeEvent) => void
}
interface AuctionTimeChangeProps {
  eventName: 'premium.AUCTION_TIME_CHANGE'
  callback: (e: AuctionTimeChange) => void
}
interface CarEnterHotbidProps {
  eventName: 'premium.CAR_ENTER_HOTBID'
  callback: (e: CarEnterHotbidEvent) => void
}
interface CarFinishedProps {
  eventName: 'premium.CAR_FINISHED'
  callback: (e: CarFinishedEvent) => void
}
interface CarHotbidResetProps {
  eventName: 'premium.CAR_HOTBID_RESET'
  callback: (e: CarHotbidResetEvent) => void
}
interface CarInPrepareProps {
  eventName: 'premium.CAR_IN_PREPARE'
  callback: (e: CarInPrepareEvent) => void
}
interface CarNotSoldProps {
  eventName: 'premium.CAR_NOT_SOLD'
  callback: (e: CarNotSoldEvent) => void
}
interface CarSoldProps {
  eventName: 'premium.CAR_SOLD'
  callback: (e: CarSoldEvent) => void
}
interface ItemReserveReleaseProps {
  eventName: 'premium.ITEM_RESERVE_RELEASE'
  callback: (e: ItemReserveReleaseEvent) => void
}
interface ItemWithdrawProps {
  eventName: 'premium.ITEM_WITHDRAW'
  callback: (e: ItemWithdrawEvent) => void
}
interface ItemDeletedProps {
  eventName: 'premium.ITEM_DELETED'
  callback: (e: ItemDeleteEvent) => void
}
interface BidOnAgentProps {
  eventName: 'premium.USER_BID_ON_AGENT'
  callback: (e: BidOnAgentEvent) => void
}

type Event = Record<string, (eventData: any) => void>

type StartListenProps = { callbackName: string } & (
  | BidOptionsProps
  | AgentSetProps
  | AuctionPublishProps
  | AuctionWithdrawProps
  | AuctionInPrepareProps
  | AuctionHiddenProps
  | AuctionFinishProps
  | AuctionVisibleProps
  | AuctionStartHotbidProps
  | CarOverbidProps
  | CarWatchedSoonInHotbidProps
  | CarActiveSoonInHotbidProps
  | UserBoughtCarProps
  | UserNotBoughtCarProps
  | DocumentPickupProps
  | UserSoldCarProps
  | CarReservedProps
  | UserNotSoldCarProps
  | UserSearchCriteriaFoundCarProps
  | DocumentInvoiceProps
  | UserBoughtCarReservedProps
  | CarWatchedFinishedProps
  | TransportReminderProps
  | UserNewNotificationProps
  | BidDeletedProps
  | ItemPublishProps
  | UserBidProps
  | AuctionPauseProps
  | AuctionResumeProps
  | AuctionTimeChangeProps
  | CarEnterHotbidProps
  | CarFinishedProps
  | CarHotbidResetProps
  | CarInPrepareProps
  | CarNotSoldProps
  | CarSoldProps
  | ItemReserveReleaseProps
  | ItemWithdrawProps
  | ItemDeletedProps
  | BidOnAgentProps
)

interface EndListenProps {
  eventName: SocketEventName
  callbackName: string
}
// #endregion

export const useWebSocketStore = defineStore('useWebSocket', () => {
  const events = ref<Partial<Record<SocketEventName, Event>>>({})
  const { $socket } = useNuxtApp()
  const getAmountOfRegisteredCallbacks = (eventName: SocketEventName) => {
    return Object.keys(events.value[eventName] ?? {}).length
  }

  const startListen = ({
    eventName,
    callbackName,
    callback,
  }: StartListenProps) => {
    if (events.value[eventName]?.[callbackName])
      return

    const amountOfRegisteredCallbacks
      = getAmountOfRegisteredCallbacks(eventName)

    events.value[eventName] = {
      ...(events.value[eventName] ?? {}),
      [callbackName]: callback,
    }

    // it's a first listened event - initialize listening
    if (!amountOfRegisteredCallbacks) {
      $socket.on(eventName, (eventData: { time: number, context: unknown }) => {
        for (const loopCallbackName in events.value[eventName]) {
          events.value[eventName][loopCallbackName](eventData)
        }
      })
    }
  }

  const endListen = ({ eventName, callbackName }: EndListenProps) => {
    delete events.value[eventName]?.[callbackName]

    const amountOfRegisteredCallbacks
      = getAmountOfRegisteredCallbacks(eventName)

    if (!amountOfRegisteredCallbacks) {
      // there is no callbacks - destroy listening
      $socket.off(eventName)
    }
  }

  return { events, startListen, endListen }
})
